import { Controller, useFormContext } from "react-hook-form";

import { DateTimePickerDropdown } from "src/base-components/DateTimePickerDropdown";
import { Input } from "src/base-components/Input";
import { Select } from "src/base-components/Select";
import { Textarea } from "src/base-components/Textarea";
import { CaseTemplateFormFieldsInner } from "src/clients/case-management";
import { assertUnreachable } from "src/utils/typeUtils";

type FormFieldProps = {
  disabled?: boolean;
  field: CaseTemplateFormFieldsInner;
  hinted?: boolean;
  onUserChange?: () => void;
};

export const FormField: React.FC<FormFieldProps> = ({
  field,
  disabled,
  hinted,
  onUserChange,
}) => {
  const form = useFormContext<Record<string, unknown>>();

  const registerWithUserChange = (key: string) => {
    const { onChange, ...rest } = form.register(key);
    const handleChange: typeof onChange = async (e) => {
      const result = await onChange(e);
      onUserChange?.();
      return result;
    };
    return { onChange: handleChange, ...rest };
  };

  switch (field.type) {
    case "text": {
      const { onChange, ...rest } = registerWithUserChange(field.key);

      if (field.display_as_textarea) {
        return (
          <Textarea
            data-loc={`task-form-field-${field.key}`}
            disabled={disabled}
            errored={!!form.formState.errors[field.key]}
            hinted={hinted}
            placeholder="Add notes"
            autoGrow
            onChange={onChange}
            {...rest}
          />
        );
      }

      return (
        <Input
          data-loc={`task-form-field-${field.key}`}
          disabled={disabled}
          errored={!!form.formState.errors[field.key]}
          hinted={hinted}
          placeholder="Add notes"
          fullWidth
          onChange={onChange}
          {...rest}
        />
      );
    }
    case "integer":
    case "number": {
      const { onChange, ...rest } = registerWithUserChange(field.key);

      return (
        <Input
          data-loc={`task-form-field-${field.key}`}
          disabled={disabled}
          errored={!!form.formState.errors[field.key]}
          hinted={hinted}
          placeholder="Add a value"
          fullWidth
          onChange={onChange}
          {...rest}
        />
      );
    }
    case "date": {
      return (
        <Controller
          control={form.control}
          name={field.key}
          render={({ field: dateField }) => {
            const selectedDate =
              typeof dateField.value === "string"
                ? new Date(dateField.value)
                : (dateField.value as Date | undefined);

            return (
              <DateTimePickerDropdown
                disabled={disabled}
                errored={!!form.formState.errors[field.key]}
                hinted={hinted}
                placeholder="Select a date"
                value={selectedDate}
                allowFutureDates
                dateOnly
                onChange={(value) => {
                  dateField.onChange(value);
                  onUserChange?.();
                }}
                onReset={() => {
                  dateField.onChange(null);
                  form.trigger(field.key);
                  onUserChange?.();
                }}
              />
            );
          }}
        />
      );
    }
    case "select": {
      return (
        <Controller
          name={field.key}
          render={({ field: selectField }) => {
            const handleChange = (value: unknown) => {
              selectField.onChange(selectField.value === value ? null : value);
              onUserChange?.();
            };

            return (
              <Select
                dataLoc={`task-form-field-${field.key}`}
                disabled={disabled}
                errored={!!form.formState.errors[field.key]}
                hinted={hinted}
                options={field.options.map((option) => ({
                  value: option,
                  key: option,
                }))}
                placeholder="Select option"
                value={selectField.value ?? ""}
                onChange={handleChange}
              />
            );
          }}
        />
      );
    }
    case "multi_select": {
      return (
        <Controller
          name={field.key}
          render={({ field: selectField }) => (
            <Select
              dataLoc={`task-form-field-${field.key}`}
              disabled={disabled}
              errored={!!form.formState.errors[field.key]}
              hinted={hinted}
              options={field.options.map((option) => ({
                value: option,
                key: option,
              }))}
              placeholder="Select option(s)"
              value={selectField.value ?? []}
              multiple
              onChange={(value) => {
                selectField.onChange(value);
                onUserChange?.();
              }}
            />
          )}
        />
      );
    }

    default: {
      assertUnreachable(field as never);
    }
  }
};
